import type { ReferenceAccount, ReferenceSite, ReferenceSnapshotData } from '../types';

export const REFERENCE_SNAPSHOT_STORAGE_KEY = 'halton_v2_reference_snapshot';
export const REFERENCE_SNAPSHOT_VERSION = 'service-laama-reference-v1' as const;

// SharePoint folder where fresh snapshots are published (D1)
export const REFERENCE_SNAPSHOT_SHAREPOINT_URL =
  'https://halton.sharepoint.com/sites/FoodserviceCRUFinland/Shared%20Documents/Operations/Import%20-%20Service%20Laama/Reference';

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

// Master data uses literal enrichment placeholders (e.g. "(pending enrichment)")
// in address fields; treat them as empty so they never reach documents or UI.
const PLACEHOLDER_RE = /^\s*\(?\s*pending enrichment\s*\)?\s*$/i;

function asCleanText(value: unknown): string {
  const text = asText(value).trim();
  return PLACEHOLDER_RE.test(text) ? '' : text;
}

function parseAccount(value: unknown, index: number): ReferenceAccount {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`accounts[${index}] must be an object`);
  }
  const record = value as Record<string, unknown>;
  const internalAccountId = asText(record.internalAccountId).trim();
  if (!internalAccountId) {
    throw new Error(`accounts[${index}].internalAccountId is required`);
  }
  return {
    internalAccountId,
    accountName: asText(record.accountName),
    vatId: asCleanText(record.vatId),
    billingStreet: asCleanText(record.billingStreet),
    billingZip: asCleanText(record.billingZip),
    billingCity: asCleanText(record.billingCity),
    invoiceEmail: asCleanText(record.invoiceEmail),
    eInvoiceAddress: asCleanText(record.eInvoiceAddress),
    eInvoiceOVT: asCleanText(record.eInvoiceOVT),
    eInvoiceOperator: asCleanText(record.eInvoiceOperator),
  };
}

function parseSite(value: unknown, index: number): ReferenceSite {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`sites[${index}] must be an object`);
  }
  const record = value as Record<string, unknown>;
  const siteLocationId = asText(record.siteLocationId).trim();
  if (!siteLocationId) {
    throw new Error(`sites[${index}].siteLocationId is required`);
  }
  return {
    siteLocationId,
    customerSite: asText(record.customerSite),
    accountId: asText(record.accountId),
    address: asCleanText(record.address),
    postalCode: asCleanText(record.postalCode),
    city: asCleanText(record.city),
  };
}

function parseLists(value: unknown): Record<string, string[]> {
  if (value === undefined || value === null) {
    return {};
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('lists must be an object');
  }
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, string[]>>(
    (acc, [key, list]) => {
      if (!Array.isArray(list)) {
        throw new Error(`lists.${key} must be an array`);
      }
      acc[key] = list.filter((item): item is string => typeof item === 'string');
      return acc;
    },
    {}
  );
}

/**
 * Validate and normalize a raw parsed reference-snapshot JSON payload.
 * Throws with a descriptive message when the payload is not a valid
 * `service-laama-reference-v1` snapshot.
 */
export function parseReferenceSnapshot(raw: unknown): ReferenceSnapshotData {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Snapshot must be a JSON object');
  }
  const record = raw as Record<string, unknown>;
  if (record.exportVersion !== REFERENCE_SNAPSHOT_VERSION) {
    throw new Error(`exportVersion must be ${REFERENCE_SNAPSHOT_VERSION}`);
  }
  const generatedAt = asText(record.generatedAt).trim();
  if (!generatedAt) {
    throw new Error('generatedAt is required');
  }
  if (!Array.isArray(record.accounts)) {
    throw new Error('accounts must be an array');
  }
  if (!Array.isArray(record.sites)) {
    throw new Error('sites must be an array');
  }

  const accounts = record.accounts.map(parseAccount);
  const sites = record.sites.map(parseSite);
  const warnings = Array.isArray(record.warnings)
    ? record.warnings.filter((item): item is string => typeof item === 'string')
    : [];

  return {
    exportVersion: REFERENCE_SNAPSHOT_VERSION,
    generatedAt,
    sourceWorkbook: asText(record.sourceWorkbook),
    counts: { accounts: accounts.length, sites: sites.length },
    warnings,
    accounts,
    sites,
    lists: parseLists(record.lists),
  };
}

export function saveReferenceSnapshot(snapshot: ReferenceSnapshotData): void {
  localStorage.setItem(REFERENCE_SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
}

export function loadReferenceSnapshot(): ReferenceSnapshotData | null {
  try {
    const data = localStorage.getItem(REFERENCE_SNAPSHOT_STORAGE_KEY);
    if (!data) {
      return null;
    }
    return parseReferenceSnapshot(JSON.parse(data));
  } catch (error) {
    console.error('Failed to load reference snapshot:', error);
    return null;
  }
}

export function clearReferenceSnapshot(): void {
  localStorage.removeItem(REFERENCE_SNAPSHOT_STORAGE_KEY);
}

/**
 * Import a reference snapshot from a user-selected JSON file and persist it.
 */
export async function importReferenceSnapshotFile(
  file: File
): Promise<{ success: true; snapshot: ReferenceSnapshotData } | { success: false; error: string }> {
  try {
    const text = await file.text();
    const snapshot = parseReferenceSnapshot(JSON.parse(text));
    saveReferenceSnapshot(snapshot);
    return { success: true, snapshot };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Invalid snapshot file',
    };
  }
}

export function findAccount(
  snapshot: ReferenceSnapshotData,
  internalAccountId: string
): ReferenceAccount | undefined {
  return snapshot.accounts.find((account) => account.internalAccountId === internalAccountId);
}

export function findSite(
  snapshot: ReferenceSnapshotData,
  siteLocationId: string
): ReferenceSite | undefined {
  return snapshot.sites.find((site) => site.siteLocationId === siteLocationId);
}

/** Sites linked to the account first, then unlinked/other sites. */
export function getSitesForAccount(
  snapshot: ReferenceSnapshotData,
  internalAccountId: string | undefined
): ReferenceSite[] {
  if (!internalAccountId) {
    return snapshot.sites;
  }
  return snapshot.sites.filter((site) => site.accountId === internalAccountId);
}

/** Case-insensitive account search over name, VAT id and IA key. */
export function searchAccounts(
  snapshot: ReferenceSnapshotData,
  query: string,
  limit = 25
): ReferenceAccount[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return snapshot.accounts.slice(0, limit);
  }
  return snapshot.accounts
    .filter((account) =>
      [account.accountName, account.vatId, account.internalAccountId].some((field) =>
        field.toLowerCase().includes(needle)
      )
    )
    .slice(0, limit);
}

/** Case-insensitive site search over site name, city and SL key. */
export function searchSites(
  snapshot: ReferenceSnapshotData,
  internalAccountId: string | undefined,
  query: string,
  limit = 25
): ReferenceSite[] {
  const scoped = getSitesForAccount(snapshot, internalAccountId);
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return scoped.slice(0, limit);
  }
  return scoped
    .filter((site) =>
      [site.customerSite, site.city, site.siteLocationId].some((field) =>
        field.toLowerCase().includes(needle)
      )
    )
    .slice(0, limit);
}
